import React from 'react';
import { Linkedin, Twitter, Lock, ShieldCheck } from 'lucide-react';

export interface FooterLink {
  label: string;
  href: string;
  isHot?: boolean; // Highlighted target tags (e.g. "New" or "Hot")
  isDisabled?: boolean; // For coming-soon items
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

// Data-driven structure for future scalability
const footerSections: FooterSection[] = [
  {
    title: 'Platform',
    links: [
      { label: 'How It Works', href: '/how-it-works' },
      { label: 'FAQ', href: '/faq-policy' },
      { label: 'Pricing', href: '/pre-launch-pricing' },
      { label: 'Contact Us', href: '/contact-us' },
      /* Uncomment in future when adding candidate/recruiter portals
      { label: 'For Candidates', href: '/for-candidates', isHot: true },
      { label: 'For Recruiters', href: '/for-recruiters' },
      */
    ],
  },
  {
    title: 'Trust & Safety',
    links: [
      { label: 'Trust & Transparency', href: '/trust-transparency', isHot: true },
      { label: 'Recruiter Verification Policy', href: '/recruiter-verification-policy' },
      { label: 'Candidate Verification Policy', href: '/candidate-verification-policy' },
      { label: 'Credit Usage Policy', href: '/credit-usage-policy' },
      /* Uncomment in future for additional trust frameworks
      { label: 'Data Protection Framework', href: '/data-protection' },
      { label: 'Verification Standards', href: '/verification-standards' },
      */
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms & Conditions', href: '/terms-and-condition' },
      { label: 'Refund & Cancellation Policy', href: '/refund-and-cancellation-policy' },
      { label: 'Cookie Policy', href: '/cookie-policy' },
      { label: 'Security Policy', href: '/security-policy' },
      /* Uncomment in future when compliance docs expand
      { label: 'GDPR / DPDP Compliance', href: '/compliance' },
      { label: 'SLA Terms', href: '/sla-terms' },
      */
    ],
  },
];

export const LandingFooter: React.FC = () => {
  return (
    <footer className="border-t border-slate-800 bg-slate-950 text-slate-400 text-sm mt-24 md:mt-32 relative overflow-hidden">
      {/* Background Accent Subtle Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[150px] bg-cyan-500/5 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Top Grid Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          
          {/* Brand Info (2 Columns on Desktop) */}
          <div className="lg:col-span-2 space-y-4 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2.5">
              <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center font-bold text-slate-950 text-xl shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                N
              </div>
              <span className="text-2xl font-extrabold text-white tracking-tight">Nexgent</span>
            </div>
            
            <p className="text-slate-400 text-xs sm:text-sm font-medium">
              The Private Leadership Hiring Platform
            </p>

            <p className="text-slate-500 text-xs max-w-sm mx-auto md:mx-0 leading-relaxed">
              Empowering executive movement through verified recruiter networks, confidential candidate profiles, and privacy-first recruitment architecture.
            </p>

            {/* Social & Trust Links */}
            <div className="flex items-center justify-center md:justify-start gap-4 pt-2 text-slate-500">
              <a href="#" className="hover:text-cyan-400 transition-colors p-1.5 rounded-lg bg-slate-900 border border-slate-800" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="#" className="hover:text-cyan-400 transition-colors p-1.5 rounded-lg bg-slate-900 border border-slate-800" aria-label="Twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="/trust-and-transparency" className="hover:text-cyan-400 transition-colors p-1.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center gap-1.5 text-xs font-mono px-2.5">
                <ShieldCheck className="w-4 h-4 text-cyan-400" />
                <span className="hidden sm:inline">Verified Platform</span>
              </a>
            </div>
          </div>

          {/* Dynamic Link Categories (3 Columns) */}
          {footerSections.map((section, idx) => (
            <div key={idx} className="text-center md:text-left space-y-3">
              <h3 className="text-xs font-mono uppercase font-semibold text-white tracking-wider">
                {section.title}
              </h3>
              <ul className="space-y-2.5">
                {section.links.map((link, linkIdx) => (
                  <li key={linkIdx}>
                    <a
                      href={link.href}
                      className={`inline-flex items-center gap-2 text-xs transition-colors ${
                        link.isDisabled
                          ? 'text-slate-600 cursor-not-allowed'
                          : 'text-slate-400 hover:text-cyan-400'
                      }`}
                    >
                      <span>{link.label}</span>
                      {link.isHot && (
                        <span className="px-1.5 py-0.2 text-[9px] font-mono uppercase font-bold bg-cyan-950 text-cyan-400 border border-cyan-800/80 rounded">
                          HOT
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>

        {/* Divider */}
        <div className="my-8 md:my-10 border-t border-slate-800/80" />

        {/* Bottom Bar Section */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500 text-center sm:text-left">
          <p>© {new Date().getFullYear()} Nexgent. All Rights Reserved.</p>
          <div className="flex items-center gap-4 text-[11px] font-mono">
            <span className="flex items-center gap-1.5 text-slate-400">
              <Lock className="w-3 h-3 text-cyan-400" /> HTTPS Secured • Privacy First
            </span>
            <span className="text-slate-700">•</span>
            <span>Motherson Infotech</span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default LandingFooter;